/**
 * A Brafton user's Google account link, used by the Google exports.
 * Mirrors GoogleConnectionController's response (never includes a token).
 */
export interface GoogleConnection {
  connected: boolean;
  /** The normalized (lowercase) @brafton.com email. */
  email: string;
  /** OAuth scopes the user granted; empty when not connected. */
  scopes: string[];
  /** ISO-8601 time the user first connected; null when not connected. */
  connectedAt: string | null;
}

/** A spreadsheet created by POST /v1/feedbackrounds/{id}/export/google-sheet. */
export interface GoogleSheetExport {
  spreadsheetId: string;
  /** Link to open the sheet in Google Sheets. */
  url: string;
  title: string;
  /** Feedback items exported (excluding the header row). */
  rowCount: number;
}

/**
 * `reason` codes on a Google connection/export error body (`{ message, reason }`).
 * Mirrors App\Exceptions\GoogleConnectionException.
 */
export type GoogleConnectionFailureReason =
  | 'not_configured'
  | 'exchange_failed'
  | 'missing_refresh_token'
  | 'missing_scope'
  | 'account_mismatch'
  | 'not_connected'
  | 'reconnect_required'
  | 'google_api_failed';

/** Reasons that mean the user has to (re)connect Google before exporting. */
export const GOOGLE_RECONNECT_REASONS: readonly GoogleConnectionFailureReason[] = [
  'not_connected',
  'reconnect_required',
];

/** A document created by POST /v1/feedbackrounds/{id}/export/google-doc. */
export interface GoogleDocExport {
  documentId: string;
  /** Link to open the document in Google Docs. */
  url: string;
  title: string;
  /** Feedback items in the document. */
  itemCount: number;
  /** Screenshots embedded as images. */
  imagesEmbedded: number;
  /** Screenshots Google couldn't fetch, linked instead of embedded. */
  imagesLinked: number;
}
